import {
  Col,
  ComponentLoader,
  ErrorSummary,
  Heading,
  HorizontalRule,
  InPageAlert,
  Row,
} from '@snsw/react-component-library';
import React, { useEffect, useState } from 'react';
import styles from './AddInspectionCertificate.module.scss';

import { PlantItemRenewalAddInspectionCertificatesPage } from 'plantItemRenewal/constants/plantItemRenewalAddInspectionCertificatesPage';
import { InspectionDocumentsUpload } from 'plantItemRenewal/pages/inspectionCertificates/InspectionDocumentsUpload';
import { NavigationButtons } from '@onegov/react-component-library';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { sanitizeHtml } from 'shared/utils/Helpers';
import { useLocation, useNavigate } from 'react-router-dom';
import { PagePath } from 'shared/constants/pagePath';
import { getInfoCodeValue, getPlantItemRenewallLicenceTypeCode } from 'plantItemRenewal/utils/PlantItemRenewalHelpers';
import { updateCertificate } from 'plantItemRenewal/redux/renewalItemsSlice';
import ScrollToTop from 'shared/utils/scrollToTop';
import { createApplication } from 'shared/redux/applicationSlice';
import { RefData } from 'plantItemNew/constants/refData';

export const AddInspectionCertificates = () => {
  ScrollToTop();

  const PAGE_CONSTANTS = PlantItemRenewalAddInspectionCertificatesPage;
  const application = useSelector((state) => state.application);
  const refData = useSelector((state) => state.referenceData.data);
  const plantItemLicenceTypes = useSelector((state) => state.plantItemLicenceTypes);

  const plantItemRenewalLicenceCode = getPlantItemRenewallLicenceTypeCode(plantItemLicenceTypes);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const item = location.state?.item;

  const engineeringCertificateCode = refData.documentType.find(
    (d) => d.description === 'Engineering Inspection certificate',
  );
  const electricalCertificateCode = refData.documentType.find(
    (d) => d.description === 'Electrical Inspection certificate',
  );

  const electricalInspectionComponentRef = React.useRef();
  const engineersInspectionComponentRef = React.useRef();

  const [electricalInspectionDetails, setElectricalInspectionDetails] = useState(item?.electricalCertificates || {});
  const [engineerInspectionDetails, setEngineerInspectionDetails] = useState(item?.engineerCertificates || {});
  const [electricalUploadErrorSummary, setElectricalUploadErrorSummary] = useState([]);
  const [engineersUploadErrorSummary, setEngineersUploadErrorSummary] = useState([]);
  const [errorSummary, setErrorSummary] = useState([]);

  const includeElectricalInstallation = getInfoCodeValue(item, 6051);

  const validateWithin1Year = (inspectionDate) => {
    if (moment(inspectionDate).isBefore(moment().subtract(1, 'year'))) {
      return 'Date of inspection must be within 12 months.';
    }
    return null;
  };

  const validateAll = () => {
    let electricalInspectionDetailValid = true;
    let engineersInspectionDetailValid = true;

    if (electricalInspectionComponentRef.current) {
      electricalInspectionDetailValid = electricalInspectionComponentRef.current.validate();
    }

    if (engineersInspectionComponentRef.current) {
      engineersInspectionDetailValid = engineersInspectionComponentRef.current.validate();
    }

    return electricalInspectionDetailValid && engineersInspectionDetailValid;
  };

  const onSaveClick = () => {
    if (validateAll()) {
      dispatch(
        updateCertificate({
          tangibleRsn: item.tangibleRsn,
          electricalCertificates: electricalInspectionDetails,
          engineerCertificates: engineerInspectionDetails,
        }),
      );
      navigate(PagePath.plantItemRenewalReviewItems, { replace: true });
    }
  };

  const onCancelClick = () => {
    navigate(PagePath.plantItemRenewalReviewItems, { replace: true });
  };

  useEffect(() => {
    setErrorSummary(electricalUploadErrorSummary.concat(engineersUploadErrorSummary));
  }, [electricalUploadErrorSummary, engineersUploadErrorSummary]);

  if (!application.createdApplication) dispatch(createApplication(plantItemRenewalLicenceCode));

  return (
    <>
      <Row style={{ marginBottom: '2rem' }}>
        <Col span={9}>
          <InPageAlert
            variant="info"
            title="If your application includes someone else’s personal details, you need to get their permission before you supply it."
          ></InPageAlert>
        </Col>
      </Row>

      <Row>
        <Col span={9}>
          {!!errorSummary?.length && <ErrorSummary errors={errorSummary} />}

          {includeElectricalInstallation === RefData.includeElectricalInstallation && (
            <>
              <Heading level={2} className={styles.noMarginT + ' ' + styles.noMarginB}>
                {PAGE_CONSTANTS.electricalInspection.title}
              </Heading>
              {sanitizeHtml(PAGE_CONSTANTS.electricalInspection.description)}
              <InspectionDocumentsUpload
                id="electricalInspection"
                ref={electricalInspectionComponentRef}
                value={electricalInspectionDetails}
                onChange={setElectricalInspectionDetails}
                onErrorChange={setElectricalUploadErrorSummary}
                validateInspectiondate={validateWithin1Year}
                labels={PAGE_CONSTANTS.electricalInspection}
                maxFiles={1}
                applicationId={application?.applicationId}
                supportingDocumentCode={electricalCertificateCode}
              />
              <div className={styles.marginTopTwoHalfRem} />
            </>
          )}

          <Heading level={2} className={styles.noMarginT + ' ' + styles.noMarginB}>
            {PAGE_CONSTANTS.mechanicalInspection.title}
          </Heading>
          {sanitizeHtml(PAGE_CONSTANTS.mechanicalInspection.description)}
          <InspectionDocumentsUpload
            id="engineersInspection"
            ref={engineersInspectionComponentRef}
            value={engineerInspectionDetails}
            onChange={setEngineerInspectionDetails}
            onErrorChange={setEngineersUploadErrorSummary}
            validateInspectiondate={validateWithin1Year}
            labels={PAGE_CONSTANTS.mechanicalInspection}
            maxFiles={1}
            applicationId={application?.applicationId}
            supportingDocumentCode={engineeringCertificateCode}
          />
        </Col>
      </Row>

      <HorizontalRule />
      <NavigationButtons
        leftButtonConfig={{
          label: 'Save',
          onClick: onSaveClick,
        }}
        rightButtonConfig={{
          label: 'Cancel',
          onClick: onCancelClick,
        }}
      />
      {application.creatingApplication && (
        <Row>
          <Col span={12}>
            <ComponentLoader fullPage />
          </Col>
        </Row>
      )}
    </>
  );
};
